import { PrismicRichText } from "@prismicio/react";
import clsx from "clsx";
import React from "react";
import { HeroLandingV2SliceFragment } from "../../types/graphql";
import { htmlSerializer } from "../../utils/htmlSerializer";
import CTA from "../cta/cta";
import CustomerQuoteRequest from "../customer-quote-request/customer-quote-request";
import GeoMessage from "../geo-message/geo-message";
import UploadFilePanel, {
  IUploadFilePanelProps,
} from "../upload-file-panel/upload-file-panel";
import { LoadingPanelInstantQuoteHero } from "../upload-file-panel/upload-file-panel.styled";
import { parseProcessConstraints } from "../../utils/parseProcessConstraints";
import {
  CTAButton,
  HeroBase,
  HeroContent,
  HeroImage,
} from "./hero-landing-v2.styled";
import { track } from "../../utils/analytics";
import SalesforceContactForm from "../salesforce-contact-form/salesforce-contact-form";
import SalesforceFormContext from "../../utils/contexts/salesforce-form-context";
import { useSalesforceViewContext } from "../../utils/contexts/salesforce-form-context";

interface IHeroLandingV2Props {
  hero: HeroLandingV2SliceFragment;
}

const LandingHeroV2 = (props: IHeroLandingV2Props) => {
  return (
    <SalesforceFormContext>
      <LandingHeroV2Context {...props} />
    </SalesforceFormContext>
  );
};

const LandingHeroV2Context = (props: IHeroLandingV2Props) => {
  const { primary } = props.hero;

  // BEGIN: Instant Quote File Upload configs
  const constraints = parseProcessConstraints(primary?.cta_link?.url);
  const uploadComponentContents = (
    <div className="!grid grid-flow-rows gap-2 text-center">
      {primary?.rfq_title && (
        <header>
          <h6>{primary?.rfq_title}</h6>
        </header>
      )}
      {primary?.rfq_subtitle && (
        <p className="text-xom-micro-copy">{primary?.rfq_subtitle}</p>
      )}
    </div>
  );
  const ctaText = "Start Your New Instant Quote";
  const ctaButton = ctaText && primary?.rfq_cta_text;
  const LoadingSkeletonCard = () => <LoadingPanelInstantQuoteHero />;
  const uploadProps = {
    panelStyle: "graySolid",
    ...constraints,
    segmentTrackPageSection: "instant-quote-hero",
    children: uploadComponentContents,
    showSupportedFileTypes: true,
    showInstantQuoteButton: true,
    instantQuoteButtonContent: ctaButton,
    layoutDirection: "vertical",
    showSecureBlurb: false,
    showPatentNotice: true,
    panelScale: "large",
    Fallback: LoadingSkeletonCard,
  } as IUploadFilePanelProps;
  // END: Instant Quote File Upload configs

  // Salesforce Form Context
  const setFormViewState = useSalesforceViewContext();

  // Talk To An Expert button
  const TTAEButton = () => (
    <div className="[&_button]:!bg-xom-yellow-100 [&_button]:!text-black [&_button]:!border-none">
      <CTA
        kind="primary"
        scale="large"
        page-section="hero"
        className="!text-lg px-4 py-2 rounded font-semibold"
        onClickFunction={() => {
          setFormViewState.setCurrentView("form");
          track("Enterprise Schedule a Consulation", {
            text: "Enterprise Lead Form Clicked",
          });
        }}
      >
        {primary?.ttae_cta_text}
      </CTA>
    </div>
  );

  // Talk To An Expert section
  const TTAECta = () => (
    <div
      className={clsx([
        "!col-span-4 md:!col-span-6 lg:!col-span-4",
        "p-8 text-center",
        "!grid !grid-flow-rows gap-4 place-items-center",
        "rounded bg-white",
        "border border-xom-blue-100",
      ])}
    >
      <div className="!grid !grid-flow-row gap-4">
        <h6>{primary?.ttae_title}</h6>
        <TTAEButton />
      </div>
    </div>
  );

  return (
    (primary && (
      <HeroBase style={{ backgroundImage: `url(${primary.hero_image?.url})` }}>
        <SalesforceContactForm
          leadSource={primary.salesforce_lead_source}
          disclaimer={primary.salesforce_form_disclaimer.richText}
        >
          <HeroImage
            image={primary.hero_image?.gatsbyImageData}
            alt={primary.hero_image?.alt || "Xometry Resources"}
            loading="lazy"
            className="hidden !block md:!hidden"
          />
          <section className="slice-section">
            <div className="container">
              <div
                className={clsx([
                  "!grid !grid-cols-4 md:!grid-cols-8 lg:!grid-cols-12 gap-4",
                ])}
              >
                <div
                  className={clsx([
                    primary?.rfq_title
                      ? "!col-span-4 lg:!col-span-9"
                      : "!col-span-4 lg:!col-span-6",
                  ])}
                >
                  <HeroContent>
                    <header>
                      <PrismicRichText
                        field={primary.primary_heading.richText}
                        components={htmlSerializer}
                      />
                    </header>
                    <div className="intro-lg text-xom-dark-75">
                      <PrismicRichText
                        field={primary.primary_description.richText}
                        components={htmlSerializer}
                      />
                    </div>
                    <div className={clsx(["ctas", primary?.cta_css_class])}>
                      {primary?.cta_text && primary?.cta_link ? (
                        <CTAButton
                          kind="primary"
                          pageSection="landing-hero-v2"
                          ctaText={primary.cta_text}
                          linkURL={primary.cta_link && primary.cta_link.url}
                          target={primary.cta_link && primary.cta_link.target}
                        />
                      ) : primary?.rfq_type === "RFQ Only" ? (
                        <div className={clsx([""])}>
                          <GeoMessage />
                          <CustomerQuoteRequest
                            buttonText="Talk To An Expert"
                            standAlone={true}
                            rfqTitle="This is the RFQ Title"
                            compact
                          />
                        </div>
                      ) : primary?.rfq_title ? (
                        <div className="!grid !grid-cols-4 md:!grid-cols-8 lg:!grid-cols-12 gap-4">
                          <div
                            className={clsx([
                              "!col-span-4 md:!col-span-6 lg:!col-span-8",
                            ])}
                          >
                            <GeoMessage />
                            <div
                              className={clsx([
                                "[&>div]:!max-w-none",
                                "[&>div>div]:!rounded [&>div>div]:!p-0 [&>div>div]:!bg-none [&>div>div]:!bg-white",
                                "[&>div>div]:!border [&>div>div]:!border-xom-blue-100",
                                "[&_button]:!bg-xom-yellow-100 [&_button]:!text-black [&_button]:!border-none [&_button]:!text-lg",
                                "[&_div[role='button']>div>div]:!px-0",
                              ])}
                            >
                              <UploadFilePanel {...uploadProps} />
                            </div>
                          </div>
                          {primary?.ttae_title && primary?.ttae_cta_text && (
                            <TTAECta />
                          )}
                        </div>
                      ) : primary?.ttae_title && primary?.ttae_cta_text ? (
                        <TTAECta />
                      ) : (
                        <TTAEButton />
                      )}
                    </div>
                  </HeroContent>
                </div>
              </div>
            </div>
          </section>
        </SalesforceContactForm>
      </HeroBase>
    )) || <></>
  );
};

export default LandingHeroV2;
